import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Typography } from "@mui/material";
import { MyHrUserTwoFactorCardTypes } from "my-hr/components";
import { MY_HR_RESET_2FA } from "my-hr/graphql";
import { OcCard, OcConfirmationDialog } from "oc/components";
import { useOcSnackbar } from "oc/hooks";
import { useOcTwoFactorEnabledState } from "oc/hooks/use-two-factor-enabled-state";

type MyHrUserTwoFactorCardProps = {
  padding?: string;
  userData: {
    email?: string;
    twoFactorEnabled?: boolean;
    // Add other fields as needed
  };
  refetchUserData: () => void;
  isMobile?: boolean;
};

export const MyHrUserTwoFactorCard = ({
  padding = "0px 16px 16px 16px",
  userData,
  refetchUserData,
  isMobile,
}: MyHrUserTwoFactorCardProps) => {
  const { t } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { setOpenMessage } = useOcSnackbar();

  const { state: twoFactorEnabled } = useOcTwoFactorEnabledState();

  const [resetTwoFactorState] = useMutation(MY_HR_RESET_2FA, {
    onCompleted() {
      refetchUserData();
      setOpenMessage({
        type: "success",
        message: t("MY_HR_TWO_FACTOR_AUTHENTICATION_RESET_SUCCESS"),
      });
      setShowConfirmDialog(false);
    },
    onError() {
      setOpenMessage({
        type: "error",
        message: t("MY_HR_TWO_FACTOR_AUTHENTICATION_RESET_FAILED"),
      });
      setShowConfirmDialog(false);
    },
  });

  const handleButtonClick = () => {
    resetTwoFactorState({
      variables: {
        email: userData?.email,
      },
    });
  };

  if (!twoFactorEnabled) {
    return null;
  }

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <Box sx={{ padding: padding }}>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleButtonClick}
        onCancel={handleDialogCancel}
        title={t("MY_HR_RESET_2FA_CONFIRM_RESET")}
        texts={[t("MY_HR_RESET_2FA_CONFIRM_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_HR_TWO_FACTOR_AUTHENTICATION")}>
        <Box height="32px" />
        <Box sx={{ width: isMobile ? "auto" : "568px", margin: "0px 16px" }}>
          <MyHrUserTwoFactorCardTypes userData={userData} />
          <Box height="36px" />
          <Button
            id="resetTwoFactorAuthentication"
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setShowConfirmDialog(true)}
            disabled={!userData?.twoFactorEnabled}
          >
            {t("MY_HR_RESET_TWO_FACTOR_AUTHENTICATION")}
          </Button>
        </Box>
        <Box height="36px" />
        <Box sx={{ width: isMobile ? "auto" : "568px", margin: "0px 16px" }}>
          <Typography variant="body1">
            {t("MY_HR_RESET_TWO_FACTOR_AUTHENTICATION_INFO")}
          </Typography>
        </Box>
        <Box height="32px" />
      </OcCard>
    </Box>
  );
};

export default MyHrUserTwoFactorCard;
